// frontend/src/components/masters/SupplierForm.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify'; // Using react-toastify for notifications
import './SupplierForm.css';
import Select2Dropdown from '../common/Select2Dropdown';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import

const API_BASE_URL = '/api/masters/suppliers/';
const COUNTRIES_API_URL = '/api/masters/countries/';

const GEONAMES_USERNAME = 'salimh.'; // Your GeoNames username for city data

// Helper to get auth token
const getAuthHeaders = (isFormData = false) => {
    const headers = {
        'Authorization': `Bearer ${localStorage.getItem('kk_token')}`
    };
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }
    // For FormData, do not set Content-Type, let the browser do it.
    return headers;
};

const SupplierForm = ({ mode, initialData, onClose }) => {
    // Form state, initialized with only the required fields
    const [supplier, setSupplier] = useState(initialData || {
        name: '',
        address: 'P.O.BOX ', // Default prefix for address
        country: '',      // Default country to Tanzania (TZ)
        city: '',
    });
    const [readOnly, setReadOnly] = useState(mode === 'view');
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const { hasPermission } = usePermissions(); // 2. Get hook

    // Refs for file preview modal (kept in case file uploads are added later, currently unused)
    const previewModalRef = useRef(null);
    const previewIframeRef = useRef(null);
    const previewImageRef = useRef(null);
    const previewDownloadDivRef = useRef(null);
    const [isMaximized, setIsMaximized] = useState(false);

    // Effect to set initial data or default values for new forms
        useEffect(() => {
        if (initialData) {
            // Ensure address has the prefix if it's missing or empty from initialData
            const addressToSet = (!initialData.address || initialData.address === '')
                ? 'P.O.BOX '
                : initialData.address;

            setSupplier({
                ...initialData,
                address: addressToSet,
                country: initialData.country || ''
            });
        } else {
            // For new forms, ensure address starts with the prefix
            setSupplier(prev => ({
                ...prev,
                address: 'P.O.BOX '
            }));
        }
        setReadOnly(mode === 'view');
    }, [mode, initialData]);

    // Resolve stored country name -> CCA2 once countries list is loaded; normalize city case
    useEffect(() => {
        if (countries.length > 0 && initialData?.country) {
            const found = countries.find(
                c => c.name.toUpperCase() === initialData.country.toUpperCase() || c.cca2 === initialData.country
            );
            if (found) {
                setSupplier(prev => ({
                    ...prev,
                    country: found.cca2,
                    city: prev.city ? prev.city.toUpperCase() : prev.city,
                }));
            }
        }
    }, [countries, initialData]);

    // --- Geolocation Data Loading ---

    const getDropdownWidth = (options, displayKey = null, minWidth = 120, maxWidth = 300) => {
        if (!options || options.length === 0) return minWidth;
        // Create a temporary span to measure text width
        const span = document.createElement('span');
        span.style.visibility = 'hidden';
        span.style.position = 'absolute';
        span.style.fontSize = '14px';
        span.style.fontFamily = 'inherit';
        document.body.appendChild(span);
        let max = minWidth;
        options.forEach(option => {
            let text = typeof option === 'string'
                ? option
                : displayKey
                    ? displayKey.split('.').reduce((o, k) => (o ? o[k] : ''), option)
                    : option?.toString() || '';
            span.textContent = text;
            max = Math.max(max, span.offsetWidth + 36); // add padding for dropdown arrow
        });
        document.body.removeChild(span);
        return Math.min(max, maxWidth);
    };

    // Callback to load countries from the internal API
    const loadCountries = useCallback(async () => {
        try {
            const token = localStorage.getItem('kk_token');
            const response = await fetch(COUNTRIES_API_URL, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setCountries(Array.isArray(data) ? data : (data.results || []));
        } catch (err) {
            console.error('Failed to load countries:', err);
            setError('Failed to load countries.');
        }
    }, []);

    // Callback to update cities based on the selected country
    const updateCities = useCallback(async (countryCode) => {
        if (!countryCode || countryCode.length !== 2) {
            setCities([]);
            return;
        }
        try {
            let url;
            if (countryCode === 'IN') {
                // For India, use ADM1 (states)
                url = `https://secure.geonames.org/searchJSON?country=IN&featureClass=A&featureCode=ADM1&maxRows=1000&orderby=adminCode1&username=${GEONAMES_USERNAME}`;
            } else {
                // For other countries, use cities
                url = `https://secure.geonames.org/searchJSON?country=${countryCode}&featureClass=P&maxRows=1000&username=${GEONAMES_USERNAME}`;
            }
            const response = await fetch(url);
            const data = await response.json();
            if (data.geonames && data.geonames.length > 0) {
                // For India, use adminName1 or name; for others, use name
                const cityList = data.geonames.map(city =>
                    countryCode === 'IN'
                        ? (city.adminName1 || city.name)
                        : city.name
                ).filter(Boolean).sort();
                setCities(cityList);
            } else {
                setCities([]); // No cities found
            }
        } catch (err) {
            console.error("Failed to load cities:", err);
            setError('Failed to load cities.'); // Display error if fetching fails
        }
    }, []);

    // Initial data loading and setup effects on component mount
    useEffect(() => {
        loadCountries(); // Load countries when component mounts
        // Removed enforceUppercaseInputs from here
    }, [loadCountries]); // Dependency array updated

    // Effect to update cities when the selected country changes
    useEffect(() => {
        if (supplier.country) {
            updateCities(supplier.country); // Call updateCities when supplier.country changes
        } else {
            setCities([]); // Clear cities if country is cleared
        }
    }, [supplier.country, updateCities]);

    // Effect to ensure address has P.O.BOX prefix on add mode or if cleared
    useEffect(() => {
        if (mode === 'add' && (!supplier.address || supplier.address === '' || supplier.address === 'P.O.BOX ')) {
            setSupplier(prev => ({ ...prev, address: 'P.O.BOX ' }));
        }
    }, [mode, supplier.address]);


    // --- Form Field Change Handler ---

    // Handles changes for all input fields (name, address, country, city)
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (readOnly) return; // Do nothing if the form is in read-only mode

        let newValue = value;
        // Directly convert to uppercase for 'name' and 'address' fields
        if (name === 'name' || name === 'address') {
            newValue = value.toUpperCase();
        }

        // Update the supplier state based on input name and value
        setSupplier(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : newValue // Handle checkboxes differently
        }));
    };


    const toggleMaximize = () => {
        if (!previewModalRef.current) return;
        const modal = previewModalRef.current;
        const btn = modal.querySelector('#maximizeBtn');

        if (!isMaximized) {
            modal.dataset.originalLeft = modal.style.left;
            modal.dataset.originalTop = modal.style.top;
            modal.dataset.originalWidth = modal.style.width;
            modal.dataset.originalHeight = modal.style.height;

            modal.style.top = '0';
            modal.style.left = '0';
            modal.style.width = '100vw';
            modal.style.height = '100vh';
            modal.style.resize = 'none';
            btn.textContent = 'Restore';
        } else {
            modal.style.top = modal.dataset.originalTop;
            modal.style.left = modal.dataset.originalLeft;
            modal.style.width = modal.dataset.originalWidth;
            modal.style.height = modal.dataset.originalHeight;
            modal.style.resize = 'both';
            btn.textContent = 'Maximize';
        }
        setIsMaximized(!isMaximized);
    };

    // Make modal draggable
    useEffect(() => {
        const modal = previewModalRef.current;
        if (!modal) return;
        const header = modal.querySelector('#modalHeader');
        let isDragging = false;
        let offsetX = 0, offsetY = 0;

        const onMouseDown = (e) => {
            isDragging = true;
            offsetX = e.clientX - modal.offsetLeft;
            offsetY = e.clientY - modal.offsetTop;
            document.body.style.userSelect = 'none';
        };
        const onMouseUp = () => {
            isDragging = false;
            document.body.style.userSelect = 'auto';
        };
        const onMouseMove = (e) => {
            if (isDragging) {
                modal.style.left = (e.clientX - offsetX) + 'px';
                modal.style.top = (e.clientY - offsetY) + 'px';
            }
        };

        if (header) {
            header.addEventListener('mousedown', onMouseDown);
            document.addEventListener('mouseup', onMouseUp);
            document.addEventListener('mousemove', onMouseMove);
        }

        return () => {
            if (header) {
                header.removeEventListener('mousedown', onMouseDown);
                document.removeEventListener('mouseup', onMouseUp);
                document.removeEventListener('mousemove', onMouseMove);
            }
        };
    }, []);

    // --- Form Submission Logic ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (readOnly) return; // Prevent submission if in read-only mode

        setError(null);    // Clear previous errors
        setMessage(null); // Clear previous success messages

        const formData = new FormData();
        // Append all current supplier state properties to FormData
        for (const key in supplier) {
            if (supplier[key] !== null && supplier[key] !== undefined) {
                // If the key is 'country', find the full country name from the 'countries' array
                // and append that instead of the CCA2 code stored in supplier.country
                if (key === 'country') {
                    const countryObject = countries.find(c => c.cca2 === supplier[key]);
                    if (countryObject) {
                        formData.append(key, countryObject.name); // Send full country name
                    } else {
                        formData.append(key, supplier[key]); // Fallback to CCA2 if name not found
                    }
                } else if (typeof supplier[key] === 'boolean') {
                    formData.append(key, supplier[key] ? 'true' : 'false');
                } else {
                    formData.append(key, supplier[key]);
                }
            }
        }
        // --- Crucial Debugging Step: Log FormData contents before sending ---
        console.log("--- FormData contents before sending (SupplierForm) ---");
        for (let pair of formData.entries()) {
            console.log(pair[0]+ ': ' + pair[1]);
        }
        console.log("-----------------------------------------------------");

        // Determine API URL and HTTP method based on whether it's an existing record
        const url = initialData ? `${API_BASE_URL}${initialData.id}/` : API_BASE_URL;
        const method = initialData ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method: method,
                headers: getAuthHeaders(true),
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage = Object.entries(errorData).map(([key, val]) => `${key}: ${val}`).join('; ');
                setError(`Save failed: ${errorMessage}`);
                toast.error(`Save failed: ${errorMessage}`);
                return;
            }

            toast.success('Supplier saved successfully!');
            if (onClose) onClose(true);

        } catch (err) {
            setError('Failed to save Supplier. Network error or server unreachable.');
            toast.error('Failed to save Supplier. Network error or server unreachable.');
        }
    };

    // Handler for the Cancel button
    const handleCancel = () => {
        onClose(false); // Notify parent component to close modal without refreshing list
    };

    // Log the current mode and readOnly state directly before render for debugging
    console.log(`[RENDER] Current mode: "${mode}", readOnly state: "${readOnly}"`);
    console.log(`[RENDER] Supplier state for Name: "${supplier.name}", Address: "${supplier.address}", Country: "${supplier.country}", City: "${supplier.city}"`);


    return (
        <div className="supplier-form-container">
            <h1>Supplier Details</h1>
            <div className="toggle-buttons">
                <button type="button" onClick={() => document.querySelectorAll('.section-content').forEach(el => el.style.display = 'block')}>Expand All</button>
                <button type="button" onClick={() => document.querySelectorAll('.section-content').forEach(el => el.style.display = 'none')}>Collapse All</button>
            </div>

            {error && <div className="form-error">{error}</div>}
            {message && <div className="form-success">{message}</div>}

            <form onSubmit={handleSubmit}>
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Supplier Information</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        <div className="row" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            {/* Name Input Field */}
                            <div style={{ flex: 2 }}>
                                <label className="required">Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={supplier.name}
                                    onChange={handleChange}
                                    required
                                    readOnly={readOnly}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            {/* Country Select2Dropdown */}
                            <div style={{ flex: 1, minWidth: 120, maxWidth: 300 }}>
                                <label className="required">Country</label>
                                <Select2Dropdown
                                    options={countries}
                                    value={supplier.country}
                                    onChange={val => handleChange({ target: { name: 'country', value: val } })}
                                    placeholder="Select Country"
                                    displayKey="name"
                                    valueKey="cca2"
                                    required
                                    disabled={readOnly}
                                    clearable={true}
                                    style={{
                                        width: getDropdownWidth(countries, 'name')
                                    }}
                                />
                            </div>
                            {/* City Select2Dropdown */}
                            <div style={{ flex: 1, minWidth: 120, maxWidth: 300 }}>
                                <label className="required">City</label>
                                <Select2Dropdown
                                    options={cities.map(c => ({ id: c, text: c }))}
                                    value={supplier.city}
                                    onChange={val => handleChange({ target: { name: 'city', value: val } })}
                                    placeholder="Select City"
                                    displayKey="text"
                                    valueKey="id"
                                    required
                                    disabled={readOnly}
                                    clearable={true}
                                    style={{
                                        width: getDropdownWidth(cities.map(c => ({ text: c })), 'text')
                                    }}
                                />
                            </div>
                        </div>
                        {/* Hidden Address Field */}
                        <input
                            type="hidden"
                            name="address"
                            value={supplier.address}
                            readOnly
                        />
                    </div>
                </div>

                <div className="buttons">
                    {/* 3. Protect Edit button */}
                    {readOnly && hasPermission('SUPPLIERS', 'can_edit') && (
                        <button type="button" onClick={() => setReadOnly(false)}>Edit</button>
                    )}
                    {/* 4. Protect Save button */}
                    {!readOnly && (
                        <button type="submit">Save</button>
                    )}
                    <button type="button" onClick={handleCancel} className="cancel-btn">Cancel</button>
                </div>
            </form>
        </div>
    );
};

export default SupplierForm;
